import Loader from '__common/components/LoaderComponent';
import React from 'react';
import throttle from 'lodash/throttle';
import YourProjectsCombineSelected from 'yourProjects/components/YourProjectsCombineSelected';
import { apiUrlUb1 } from 'local_config';
import { connect } from 'react-redux';
import { parseDate } from '__common/calculations/dateFormat';
import {
  GET_USER_PROJECTS,
  SET_YOUR_PROJECTS_PAGINATION_START,
  SET_YOUR_PROJECTS_SORTING,
  SET_YOUR_PROJECTS_TABLE_PAGE,
  SEARCH_PROJECT,
  DELETE_PROJECT,
  COPY_PROJECT,
  SET_SELECTED_PROJECTS,
} from 'actions';
import { Button, Table, TableHead, TableBody, TableRow, TableCell, TablePagination, TableContainer, Paper, Checkbox, Tooltip } from '@mui/material';
import { OPEN_CONFIRM_CLEAR_ARRAYS_MODAL } from 'actions';
import { showSuccessAlert } from '__common/modules/alerts';


type Props = {
  sortedField: string;
  sortMethod: string;
  rowsPerPage: number;
  data: any[];
  dispatch: Function;
  paginationStart: number;
  totalProjects: number;
  filterString: string;
  paginationPage: number;
  paginationTotal: number;
  selectedProjects: { [projectId: string]: boolean };
  showOldProjects: boolean;
  areLoading: boolean;
  appliedFilters: {},
};

class YourProjectsTable extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.deleteProject = throttle(this.deleteProject, 1000);
    this.copyProject = throttle(this.copyProject, 1000);
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(SET_SELECTED_PROJECTS({}));
  }

  sort = (field: string) => {
    const {
      dispatch,
      rowsPerPage,
      paginationStart,
      sortedField,
      sortMethod,
      filterString,
      showOldProjects,
      appliedFilters,
    } = this.props;
    if (field === sortedField) {
      const sort = sortMethod === 'asc' ? 'desc' : 'asc';
      dispatch(SET_YOUR_PROJECTS_SORTING(sortedField, sort));
      dispatch(
        GET_USER_PROJECTS(
          rowsPerPage,
          paginationStart,
          field,
          sort,
          filterString,
          showOldProjects,
          appliedFilters
        ),
      );
    } else {
      dispatch(SET_YOUR_PROJECTS_SORTING(field, 'asc'));
      dispatch(
        GET_USER_PROJECTS(
          rowsPerPage,
          paginationStart,
          field,
          'asc',
          filterString,
          showOldProjects,
          appliedFilters
        ),
      );
    }
  }

  handlePagination = (
    paginationStart: number,
    rowsPerPage: number,
    currentPage: number,
  ) => {
    const {
      dispatch,
      sortedField,
      sortMethod,
      filterString,
      showOldProjects,
      appliedFilters,
    } = this.props;
    dispatch(SET_YOUR_PROJECTS_PAGINATION_START(paginationStart));
    dispatch(SET_YOUR_PROJECTS_TABLE_PAGE(currentPage));
    dispatch(
      GET_USER_PROJECTS(
        rowsPerPage,
        paginationStart,
        sortedField,
        sortMethod,
        filterString,
        showOldProjects,
        appliedFilters,
      ),
    );
  }

  goToProject = (projectId: string, absolute_url: number) => {
    const { dispatch, showOldProjects } = this.props;

    if (showOldProjects) {
      window.open(`https://${apiUrlUb1.slice(0, -1)}${absolute_url}`, '_blank');
    } else {
      dispatch(SEARCH_PROJECT(projectId));
    }
  }

  copyProject = (projectId: string) => {
    const { dispatch } = this.props;
    dispatch(COPY_PROJECT(projectId));
  }

  deleteProject = (projectId: string) => {
    const {
      dispatch,
      rowsPerPage,
      paginationStart,
      sortedField,
      sortMethod,
      filterString,
      showOldProjects,
      appliedFilters,
    } = this.props;

    const postDeleteAction = GET_USER_PROJECTS.bind(
      null,
      rowsPerPage,
      paginationStart,
      sortedField,
      sortMethod,
      filterString,
      showOldProjects,
      appliedFilters,
    );

    dispatch (
      OPEN_CONFIRM_CLEAR_ARRAYS_MODAL(
        `Are you sure you want to delete the project?`,
        `All project details will be lost once deleted.
        `,
        () => {
                dispatch(DELETE_PROJECT(postDeleteAction, projectId));
                showSuccessAlert('Project deleted successfully')
        },
      ),
  )}
  

  copyAndDeleteButtons(projectId: string) {
    if (this.props.showOldProjects) {
      return null;
    }

    return (
      <TableCell className="utils" sx={{ whiteSpace: 'nowrap', padding: '8px 16px' }}>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Tooltip title="Duplicate this project" arrow placement="top">
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={this.copyProject.bind(null, projectId)}
              className="copy-button"
              startIcon={<i className="material-icons" style={{ fontSize: '16px' }}>content_copy</i>}
              sx={{
                minWidth: 'auto',
                padding: '4px 8px',
                fontSize: '12px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  borderColor: '#1976d2',
                }
              }}
            >
              Copy
            </Button>
          </Tooltip>
          <Tooltip title="Delete this project permanently" arrow placement="top">
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={this.deleteProject.bind(null, projectId)}
              className="delete-button"
              startIcon={<i className="material-icons" style={{ fontSize: '16px' }}>delete</i>}
              sx={{
                minWidth: 'auto',
                padding: '4px 8px',
                fontSize: '12px',
                textTransform: 'none',
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                  borderColor: '#d32f2f',
                }
              }}
            >
              Delete
            </Button>
          </Tooltip>
        </div>
      </TableCell>
    );
  }

  totalPrice = (projectId: string, totalPrice: number) => {
    if (!totalPrice || totalPrice === 0) {
      return (
        <div style={{
          color: '#d32f2f',
          fontStyle: 'italic',
          fontSize: '13px',
          fontWeight: 500
        }}>
          BOM not calculated
        </div>
      );
    }

    return (
      <div style={{
        color: '#2e7d32',
        fontWeight: 600,
        fontSize: '14px'
      }}>
        ${totalPrice.toLocaleString()}
      </div>
    );
  }

  combineSelected = () => {
    const {
      dispatch,
      selectedProjects,
    } = this.props;

    return (
    <YourProjectsCombineSelected
      dispatch={dispatch}
      selectedProjects={selectedProjects}
    />
    );
  }

  tableBottom = () => {
    const {
      rowsPerPage,
      paginationTotal,
      paginationPage,
    } = this.props;

    return (
      <>
        {this.combineSelected()}
        <TablePagination
          component="div"
          count={paginationTotal}
          page={paginationPage}
          onPageChange={(event, newPage) => this.handlePagination(newPage * rowsPerPage, rowsPerPage, newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            this.handlePagination(0, newRowsPerPage, 0);
          }}
        />
      </>
    );
  }

  setSelected = (rowId: number, selected: boolean) => {
    const { data, dispatch, selectedProjects } = this.props;

    let selectedRows = { ...selectedProjects };

    if (rowId === 0) {
      selectedRows = data.reduce((acc, project) => {
        if (project.total_price === 0) {
          return acc;
        }

        if (selected) {
          acc[project.project_id] = true;
        } else {
          acc[project.project_id] = false;
        }

        return acc;
      }, {});
    } else {
      const projectId = data[rowId - 1].project_id;
      selectedRows[projectId] = selected;
    }

    dispatch(SET_SELECTED_PROJECTS(selectedRows));
  }

  render() {
    const {
      data,
      sortedField,
      sortMethod,
      selectedProjects,
      areLoading,
      rowsPerPage,
      paginationTotal,
      paginationPage,
    } = this.props;

    if (areLoading) {
      return <Loader />;
    }

    if (!data || data.length === 0) {
      return (<div className="main-title">No projects found</div>);
    }

    const selectableProjects = data.filter(p => p.total_price > 0);
    const selectedCount = selectableProjects.filter(p => selectedProjects[p.project_id]).length;
    const totalSelectable = selectableProjects.length;

    return (
      <>
        {selectedCount > 0 && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            borderRadius: '4px 4px 0 0',
            color: '#1976d2',
            fontSize: '14px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <i className="material-icons" style={{ fontSize: '18px' }}>check_circle</i>
            {selectedCount} of {totalSelectable} projects selected
          </div>
        )}
        <TableContainer 
          className="projects-table-container"
          component={Paper} 
          sx={{ 
            borderRadius: selectedCount > 0 ? '0' : '4px 4px 0 0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            borderBottom: 'none'
          }}
        >
          <Table className="your-projects-table" stickyHeader size="small">
          <TableHead>
            <TableRow className="projects-table-header" sx={{
              backgroundColor: '#fafafa',
              '& .MuiTableCell-head': {
                backgroundColor: '#fafafa',
                borderBottom: '2px solid #e0e0e0',
                fontWeight: 600,
                color: '#424242',
                textTransform: 'uppercase',
              }
            }}>
              <TableCell style={{ fontWeight: 'bold', width: 56, textAlign: 'center' }}>
                <Tooltip 
                  title={(() => {
                    const selectableProjects = data.filter(p => p.total_price > 0);
                    const selectedCount = selectableProjects.filter(p => selectedProjects[p.project_id]).length;
                    const totalSelectable = selectableProjects.length;
                    
                    if (totalSelectable === 0) return "No selectable projects";
                    if (selectedCount === 0) return "Select all projects";
                    if (selectedCount === totalSelectable) return "Deselect all projects";
                    return `${selectedCount}/${totalSelectable} selected - Click to select all`;
                  })()}
                  arrow
                  placement="top"
                >
                  <Checkbox
                    indeterminate={(() => {
                      const selectableProjects = data.filter(p => p.total_price > 0);
                      const selectedSelectableProjects = selectableProjects.filter(p => selectedProjects[p.project_id]);
                      return selectedSelectableProjects.length > 0 && selectedSelectableProjects.length < selectableProjects.length;
                    })()}
                    checked={(() => {
                      const selectableProjects = data.filter(p => p.total_price > 0);
                      return selectableProjects.length > 0 && selectableProjects.every(p => selectedProjects[p.project_id]);
                    })()}
                    onChange={(event) => this.setSelected(0, event.target.checked)}
                    disabled={data.filter(p => p.total_price > 0).length === 0}
                    size="small"
                    sx={{ marginLeft: '7.5px' }}
                  />
                </Tooltip>
              </TableCell>
              <TableCell
                onClick={this.sort.bind(null, 'project_name')}
                style={{ cursor: 'pointer', fontWeight: 'bold', color: sortedField === 'project_name' ? '#d32f2f' : '#424242' }}
              >
                <span className="projects-table-header-label">
                  PROJECT NAME
                  <i className="sort-arrow md-icon material-icons md-text--inherit" style={{color: sortedField === 'project_name' ? '#d32f2f' : '#424242'}}>
                    {sortMethod === 'asc' ? 'arrow_drop_down' : 'arrow_drop_up'}
                  </i>
                </span>
              </TableCell>
              <TableCell
                onClick={this.sort.bind(null, 'client_name')}
                style={{ cursor: 'pointer', fontWeight: 'bold', color: sortedField === 'client_name' ? '#d32f2f' : '#424242' }}
              >
                <span className="projects-table-header-label">
                  CLIENT NAME
                  <i className="sort-arrow md-icon material-icons md-text--inherit" style={{color: sortedField === 'client_name' ? '#d32f2f' : '#424242'}}>
                    {sortMethod === 'asc' ? 'arrow_drop_down' : 'arrow_drop_up'}
                  </i>
                </span>
              </TableCell>
              <TableCell
                onClick={this.sort.bind(null, 'created')}
                style={{ cursor: 'pointer', fontWeight: 'bold', color: sortedField === 'created' ? '#d32f2f' : '#424242' }}
              >
                <span className="projects-table-header-label">
                  CREATED
                  <i className="sort-arrow md-icon material-icons md-text--inherit" style={{color: sortedField === 'created' ? '#d32f2f' : '#424242'}}>
                    {sortMethod === 'asc' ? 'arrow_drop_down' : 'arrow_drop_up'}
                  </i>
                </span>
              </TableCell>
              <TableCell
                onClick={this.sort.bind(null, 'panel_mfg')}
                style={{ cursor: 'pointer', fontWeight: 'bold', color: sortedField === 'panel_mfg' ? '#d32f2f' : '#424242' }}
              >
                <span className="projects-table-header-label">
                  MFG
                  <i className="sort-arrow md-icon material-icons md-text--inherit" style={{color: sortedField === 'panel_mfg' ? '#d32f2f' : '#424242'}}>
                    {sortMethod === 'asc' ? 'arrow_drop_down' : 'arrow_drop_up'}
                  </i>
                </span>
              </TableCell>
              <TableCell
                onClick={this.sort.bind(null, 'panel_model')}
                style={{ cursor: 'pointer', fontWeight: 'bold', color: sortedField === 'panel_model' ? '#d32f2f' : '#424242' }}
              >
                <span className="projects-table-header-label">
                  MODEL
                  <i className="sort-arrow md-icon material-icons md-text--inherit" style={{color: sortedField === 'panel_model' ? '#d32f2f' : '#424242'}}>
                    {sortMethod === 'asc' ? 'arrow_drop_down' : 'arrow_drop_up'}
                  </i>
                </span>
              </TableCell>
              <TableCell
                onClick={this.sort.bind(null, 'zipcode')}
                style={{ cursor: 'pointer', fontWeight: 'bold', color: sortedField === 'zipcode' ? '#d32f2f' : '#424242' }}
              >
                <span className="projects-table-header-label">
                  ZIP
                  <i className="sort-arrow md-icon material-icons md-text--inherit" style={{color: sortedField === 'zipcode' ? '#d32f2f' : '#424242'}}>
                    {sortMethod === 'asc' ? 'arrow_drop_down' : 'arrow_drop_up'}
                  </i>
                </span>
              </TableCell>
              <TableCell
                onClick={this.sort.bind(null, 'total_price')}
                style={{ cursor: 'pointer', fontWeight: 'bold', color: sortedField === 'total_price' ? '#d32f2f' : '#424242' }}
              >
                <span className="projects-table-header-label">
                  LIST
                  <i className="sort-arrow md-icon material-icons md-text--inherit" style={{color: sortedField === 'total_price' ? '#d32f2f' : '#424242'}}>
                    {sortMethod === 'asc' ? 'arrow_drop_down' : 'arrow_drop_up'}
                  </i>
                </span>
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((project, index) => (
              <TableRow
                key={project.project_id || index}
                className={project.total_price > 0 ? '' : 'bom-not-calculated'}
                selected={(selectedProjects[project.project_id] && project.total_price > 0) || false}
                onClick={(event) => {
                  const target = event.target as HTMLElement;
                  if (!target.closest('.project-name') && !target.closest('button') && project.total_price > 0) {
                    this.setSelected(index + 1, !selectedProjects[project.project_id]);
                  }
                }}
                sx={{
                  backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.02)',
                  '&:hover': {
                    backgroundColor: '#f1f1f1',
                    cursor: project.total_price === 0 ? 'not-allowed' : 'pointer'
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#f1f1f1',
                    '&:hover': {
                      backgroundColor: '#e8e8e8',
                    }
                  },
                  opacity: project.total_price === 0 ? 0.7 : 1,
                  transition: 'all 0.2s ease',
                  '& .MuiTableCell-body': {
                    borderBottom: '1px solid rgba(224, 224, 224, 0.8)',
                    fontSize: '14px',
                    padding: '8px 12px'
                  }
                }}
              >
                <TableCell style={{ textAlign: 'center', padding: '8px' }}>
                  <Tooltip 
                    title={(() => {
                      if (project.total_price === 0) {
                        return "Cannot select: BOM not calculated";
                      }
                      return selectedProjects[project.project_id] 
                        ? "Click to deselect project" 
                        : "Click to select project";
                    })()} 
                    arrow
                    placement="right"
                    enterDelay={300}
                  >
                    <span style={{ 
                      display: 'inline-block',
                      opacity: project.total_price === 0 ? 0.4 : 1,
                      transition: 'opacity 0.2s ease'
                    }}>
                      <Checkbox
                        checked={selectedProjects[project.project_id] || false}
                        disabled={project.total_price === 0}
                        onChange={(event) => this.setSelected(index + 1, event.target.checked)}
                        size="small"
                        color="primary"
                        sx={{
                          marginLeft:"7.5px",
                          '&:hover': {
                            backgroundColor: project.total_price === 0 ? 'transparent' : 'rgba(25, 118, 210, 0.04)',
                          },
                          '&.Mui-disabled': {
                            color: 'rgba(0, 0, 0, 0.26)',
                          }
                        }}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  onClick={this.goToProject.bind(
                    null,
                    project.project_id,
                    project.absolute_url,
                  )}
                  className="project-name"
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    userSelect: 'none'
                  }}
                >
                  <Tooltip 
                    title="Click to open project" 
                    arrow 
                    placement="top"
                    enterDelay={500}
                  >
                    <div>
                      {project.project_name} - <span className="project-id" style={{ fontWeight: 'bold', color: '#666' }}>#{project.project_id.toUpperCase()}</span>
                    </div>
                  </Tooltip>
                </TableCell>
                <TableCell>{project.client_name}</TableCell>
                <TableCell>{parseDate(project.created)}</TableCell>
                <TableCell>{project.panel_mfg}</TableCell>
                <TableCell>{project.panel_model}</TableCell>
                <TableCell>{project.zipcode}</TableCell>
                <TableCell>
                  {this.totalPrice(project.project_id, project.total_price)}
                </TableCell>
                {this.copyAndDeleteButtons(project.project_id)}
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </TableContainer>
        <div className="table-footer">
          <YourProjectsCombineSelected
            dispatch={this.props.dispatch}
            selectedProjects={selectedProjects}
          />
          <TablePagination
            component="div"
            count={paginationTotal}
            page={paginationPage}
            onPageChange={(event, newPage) => this.handlePagination(newPage * rowsPerPage, rowsPerPage, newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          />
        </div>
      </>
    );
  }
}

function mapStateToProps(appState: appState) {
  return {
    yourProjects: appState.yourProjects,
  };
}

export default connect(mapStateToProps)(YourProjectsTable);
